import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { Input, Button, Icon } from 'semantic-ui-react';
import { useIntl } from 'react-intl';
import { debounce } from 'lodash';
import findResult from '@/utils/resultsHelper';
import { states } from '@/constants/lifecycle';
import { updateSearchProperties } from '../actions';
import { performFacetedSearch } from '../searchHelper';
import CopySearchUrl from '../components/CopySearchUrl';
import { searchPermissions } from '../constants';

const SearchInput = ({
  code,
  category,
  search,
  query,
  filter,
  searchStatus,
  canUpdateSearch,
  updateTerm,
  compact,
  filtersVisible,
}) => {
  const intl = useIntl();
  const debounceSearch = useMemo(() => debounce(search, 500), [search]);

  const onChange = (value) => {
    if (canUpdateSearch) {
      updateTerm(category, code, { query: value });
      debounceSearch(category, code);
    }
  };

  const onKeydown = (e) => {
    if (e.key === 'Enter') {
      search(category, code);
    }
  };

  const toggleFiltersVisible = () => {
    updateTerm(category, code, {
      filtersVisible: !filtersVisible,
    });
  };

  const getLabel = () => (
    <Button.Group icon compact className="primary">
      <CopySearchUrl query={query} filter={filter} />
      {compact && (
        <Button onClick={toggleFiltersVisible} compact>
          <Icon name="filter" className="primary" />
        </Button>
      )}
    </Button.Group>
  );

  return (
    <Input
      fluid
      size="large"
      className="aridhia-search-input primary"
      icon="search"
      iconPosition="left"
      placeholder={intl.formatMessage({ id: 'search.input.placeholder' })}
      labelPosition="right"
      label={getLabel()}
      autoFocus
      value={query}
      onChange={(e, { value }) => (onChange(value))}
      onKeyDown={onKeydown}
      loading={searchStatus === states.PENDING}
    />
  );
};

const accessControl = (role, exists, item = null) => (
  {
    canUpdateSearch: exists ? role.permissions.search.run && item.permissions[searchPermissions.UPDATE] : false,
  }
);

const withoutItem = (role) => (
  {
    ...accessControl(role, false),
    query: '',
    filter: {},
    searchStatus: states.NONE,
    filtersVisible: false,
  }
);

const withItem = (item, role) => (
  {
    ...accessControl(role, true, item),
    query: item.query,
    filter: item.filter,
    searchStatus: item.searchStatus,
    filtersVisible: item.filtersVisible,
  }
);

const mapStateToProps = ({
  auth: { role },
  search: { searches },
}, { category, code }) => {
  const item = findResult(searches, category, code);
  return item ? withItem(item, role) : withoutItem(role);
};

const mapDispatchToProps = {
  updateTerm: updateSearchProperties,
  search: performFacetedSearch,
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchInput);
